import React, { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ColorField } from '@/components/inspector-fields/ColorField/ColorField';
import StylesBgColorSettings from '@/components/inspector-fields/ColorField/StylesBgColorSettings';
import { isColorReference, processColorUpdate } from '@builder4/shared/utils/styleHelpers';
import type { Color } from '@builder4/shared/types/layout';
import { useColor } from '@/hooks/useColor';
import { SliderField } from '@/components/inspector-fields/SliderField/SliderField';
import StylesFontWeightSettings from '@/components/dropdowns/SelectField/StylesFontWeightSettings';
import type { BodyTypography } from '@builder4/shared/types/styles';
import { useAppSelector } from '@/store/hooks';
import { selectEditingStyleColors } from '@/store/globalStyles/selectors';
import { createPaletteFromGlobalColors } from '@/utils/colorPaletteHelpers';

type BodyVariant = 'large' | 'medium' | 'small';

interface StylesInspectorBodyProps {
  className: string;
  onClick?: () => void;
  bodyVariant: BodyVariant;
  onSizeChange?: (value: number) => void;
  currentData: BodyTypography;
  onUpdate: (data: BodyTypography) => void;
}

export const StylesInspectorBody: React.FC<StylesInspectorBodyProps> = ({
  className,
  onClick,
  bodyVariant,
  onSizeChange,
  currentData: bodyData,
  onUpdate,
}) => {
  const [selectedFontFamily] = useState<string>('Inter');

  const rootRef = useRef<HTMLDivElement | null>(null);
  const sizeLevel = bodyData[bodyVariant];
  const lineHeight = sizeLevel.lineHeight;
  const currentWeight = sizeLevel.fontWeight;
  const currentLetterSpacing = sizeLevel.letterSpacing;

  const styleColors = useAppSelector(selectEditingStyleColors);
  const palettesForPicker = useMemo(
    () => (styleColors ? [createPaletteFromGlobalColors(styleColors)] : []),
    [styleColors],
  );

  const fontColor = useColor(sizeLevel.fontColor, styleColors ?? undefined);
  const backgroundColor = useColor(sizeLevel.backgroundColor, styleColors ?? undefined);

  const handleClick = () => onClick?.();

  const updateVariant = (props: Partial<BodyTypography[BodyVariant]>) => {
    onUpdate({
      ...bodyData,
      [bodyVariant]: { ...sizeLevel, ...props },
    });
  };

  const handleOnLineHeightChange = (value: number) => {
    updateVariant({ lineHeight: value });
  };

  const handleFontSizeChange = (val: number) => {
    onSizeChange?.(val);
    updateVariant({ fontSize: val });
  };

  const fieldWidthClass = 'w-full';

  const handleWeightChange = (weight: string) => {
    updateVariant({ fontWeight: weight });
  };

  const handleLetterSpacingChange = (spacing: number) => {
    updateVariant({ letterSpacing: spacing });
  };

  const handleColorChange = (value: Color | string) => {
    if (typeof value === 'string' && isColorReference(value)) {
      updateVariant({ fontColor: value });
      return;
    }
    if (typeof value === 'string') return;
    const processed = processColorUpdate(value);
    if (processed === undefined || processed === null) return;
    updateVariant({ fontColor: processed });
  };

  const handleBgColorChange = (value: Color | string | null | undefined) => {
    if (value === null || value === undefined) {
      updateVariant({ backgroundColor: null });
      return;
    }
    if (typeof value === 'string' && isColorReference(value)) {
      updateVariant({ backgroundColor: value });
      return;
    }
    if (typeof value === 'string') return;
    const processed = processColorUpdate(value);
    if (processed === undefined) return;
    updateVariant({ backgroundColor: processed });
  };

  return (
    <div
      onClick={handleClick}
      style={{ width: 248 }}
      ref={rootRef}
      className={clsx('flex flex-col gap-3', className)}
    >
      <div className="flex flex-col gap-3 p-3">
        {/* Text Size using base SliderField */}
        <SliderField
          key={`body-${bodyVariant}`}
          label="Text Size"
          value={sizeLevel.fontSize}
          onChange={handleFontSizeChange}
          min={0}
          max={100}
          suffix="px"
        />

        <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

        <p className="pb-1 text-xs font-semibold text-neutral-900">Text</p>

        <StylesFontWeightSettings
          className={'w-full'}
          value={currentWeight}
          fontFamily={selectedFontFamily}
          onChange={handleWeightChange}
        />

        {/* Inline NodeColorSettings UI */}
        <ColorField
          hasGradient={true}
          canClear={false}
          className={fieldWidthClass}
          inputValue={fontColor.hexForInput}
          placeholderText="-"
          state="default"
          text="Color"
          type="text"
          color={fontColor.resolvedColor || undefined}
          gradient={fontColor.gradient}
          mode={fontColor.mode}
          palettes={palettesForPicker}
          onChange={handleColorChange}
          onChangeComplete={handleColorChange}
        />
        <StylesBgColorSettings
          className={fieldWidthClass}
          text="Background Color"
          value={backgroundColor.resolvedColor}
          palettes={palettesForPicker}
          onChange={handleBgColorChange}
          onChangeComplete={handleBgColorChange}
        />
        <SliderField
          className="w-full"
          label="Line Height"
          value={lineHeight || 0}
          onChange={handleOnLineHeightChange}
          min={0}
          max={5}
          allowDecimal={true}
        />
        {/* Inline NodeSpaceSettings UI */}
        <SliderField
          label="Spacing"
          value={currentLetterSpacing || 0}
          onChange={handleLetterSpacingChange}
          onChangeComplete={handleLetterSpacingChange}
          min={-2}
          max={2}
          step={0.01}
          allowDecimal={true}
        />
      </div>
    </div>
  );
};

export default StylesInspectorBody;
